import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, LogIn, Mail, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API, apiCall } from "@/lib/api";

/**
 * Login dialog with two flows:
 *  - Email + password (submits to POST /api/auth/login)
 *  - Google via Emergent-managed OAuth (redirects to auth.emergentagent.com)
 * Both paths end up with the same `session_token` cookie, so downstream code is
 * agnostic to which flow the user chose.
 */
export const LoginDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Ingresá email y contraseña");
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data, status } = await apiCall("POST", `${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (ok) {
        toast.success("Bienvenido");
        onOpenChange(false);
        navigate("/dashboard", { replace: true, state: { user: data } });
      } else if (status === 429) {
        setError(data?.detail || "Demasiados intentos. Esperá unos minutos.");
      } else {
        setError(data?.detail || "Credenciales inválidas");
      }
    } catch (err) {
      setError("Error de conexión, intentá de nuevo");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-zinc-950 border-white/10" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <LogIn className="w-5 h-5 text-violet-400" />Iniciar sesión
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">Elegí Google o iniciá con tu email y contraseña.</DialogDescription>
        </DialogHeader>

        {/* Google */}
        <Button
          type="button"
          onClick={handleGoogleLogin}
          className="w-full bg-white text-zinc-900 hover:bg-zinc-100 border border-zinc-300 font-medium"
          data-testid="login-google-btn"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar con Google
        </Button>

        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-zinc-950 px-2 text-zinc-500">o con email</span></div>
        </div>

        {/* Email + password */}
        <form onSubmit={handleEmailLogin} className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1"><Mail className="w-3 h-3" />Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@empresa.com"
              className="bg-zinc-900 border-zinc-800 text-white"
              autoComplete="email"
              disabled={submitting}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block flex items-center gap-1"><Lock className="w-3 h-3" />Contraseña</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-zinc-900 border-zinc-800 text-white"
              autoComplete="current-password"
              disabled={submitting}
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-2 bg-rose-500/15 border border-rose-500/40 rounded text-xs text-rose-600 dark:text-rose-300 font-medium" data-testid="login-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="login-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
            Iniciar sesión
          </Button>
          <p className="text-[11px] text-zinc-500 text-center">¿Sin cuenta? Contactá al superadmin para que te dé de alta.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
